import React from 'react';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram } from 'lucide-react';
import { businessInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 border-t border-slate-800">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">ADP</span>
              </div>
              <div>
                <h3 className="text-white font-bold text-xl leading-tight">ADP Motors</h3>
                <p className="text-blue-400 text-xs">& Recovery</p>
              </div>
            </div>
            <p className="text-gray-400 mb-6">
              Professional vehicle recovery and roadside assistance services across the UK. 
              Available 24/7 for all your emergency needs.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-200">
                <Facebook className="w-5 h-5 text-gray-400 hover:text-white" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-200">
                <Twitter className="w-5 h-5 text-gray-400 hover:text-white" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-200">
                <Instagram className="w-5 h-5 text-gray-400 hover:text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => document.getElementById('home').scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
                >
                  Home
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
                >
                  Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
                >
                  About Us
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('testimonials').scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Our Services</h4>
            <ul className="space-y-3">
              <li className="text-gray-400">24/7 Breakdown Recovery</li>
              <li className="text-gray-400">Vehicle Towing</li>
              <li className="text-gray-400">Roadside Assistance</li>
              <li className="text-gray-400">Accident Recovery</li>
              <li className="text-gray-400">Jump Starts</li>
              <li className="text-gray-400">Flat Battery Service</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-500 text-sm">Emergency 24/7</p>
                  <a href={`tel:${businessInfo.phone}`} className="text-white hover:text-blue-400 transition-colors">
                    {businessInfo.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-500 text-sm">Email</p>
                  <a href={`mailto:${businessInfo.email}`} className="text-white hover:text-blue-400 transition-colors break-all">
                    {businessInfo.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-500 text-sm">Coverage</p>
                  <p className="text-white">Nationwide UK</p>
                  <p className="text-gray-400 text-sm">Based in Bolton</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © {currentYear} ADP Motors & Recovery. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-500 hover:text-blue-400 text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-500 hover:text-blue-400 text-sm transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Emergency Button */}
      <a
        href={`tel:${businessInfo.phone}`}
        className="fixed bottom-8 right-8 z-50 flex items-center space-x-3 bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-full font-bold shadow-2xl hover:scale-110 transition-all duration-200 animate-pulse hover:animate-none"
      >
        <Phone className="w-5 h-5" />
        <span className="hidden md:inline">Emergency Call</span>
      </a>
    </footer>
  );
};

export default Footer;
